import { supabase } from './supabase';
import { VENDOR_COLUMNS } from './vendorCatalogExport';
import { VENDOR_CATALOG_COLUMNS, ITEM_COLUMNS, templateCsv } from './masterDataTemplates';
import { fetchUnits, fetchCategories } from './taxonomy';
import { buildImportWorkbook, workbookFileName, WORKBOOK_MIME } from './importWorkbook';
import { canImportVendorCatalog, canEditVendorPrice, canGovernCatalogItem } from './permissions';
import * as vendorList from './vendorCatalogImport';
import * as vendorCatalog from './vendorItemsImport';
import * as itemCatalog from './itemCatalogImport';

// Issue #422, Session 78s.1. Gate decision M22 — the descriptor.
//
// In plain terms: there is one Import screen, and what it is importing is a
// choice made at the top of it. Everything after that choice looks and behaves
// the same, because the differences between "a list of vendors" and "a vendor's
// price list" are written down here as data rather than as `if` statements
// scattered through the screen.
//
// WHY THIS FILE EXISTS AT ALL, and M22 argues it as a decision rather than an
// implementation detail, because getting it wrong is what turns a good
// component into the file nobody wants to open. MasterDataImportSheet.js — which
// was VendorCatalogImportSheet.js until this session — is a state machine
// (choose, map, ask, preview, result) and it was ~600 lines doing ONE job. Three jobs written as `if (type === …)` throughout would be
// ~1,200 lines and worse than duplicating outright.
//
// THE TEST OF WHETHER THE SPLIT IS IN THE RIGHT PLACE: a fourth import type
// should be a new object in this file and NO edit to the flow. If it is not,
// the split was drawn wrong. The item-catalog descriptor lands at Session 78t
// and is the first real use of that claim.
//
// WHAT A DESCRIPTOR OWNS: its columns, how a row finds the record it is about,
// what a preview row says, how it is written, and the permission that gates it.
// WHAT IT DOES NOT OWN: parsing, column matching, the tallies, the preview
// layout, the rejected-rows file. Those are the same for everything and live in
// importCore.js and in the component.
//
// WHAT DOES NOT SHARE THIS COMPONENT, deliberately: adding one vendor by hand,
// or attaching one item to a vendor by hand. Those stay exactly where they are.

// ── The blank workbook ───────────────────────────────────────────────────────
//
// Session 78u. The component asks for a template and gets one; whether that is
// a .xlsx or a .csv is a property of the descriptor, which is M22 holding for
// the third time - MasterDataImportSheet.js does not branch on import type.
//
// W15. THE FILE IS STEDE'S AND THE CUSTOMER IS A VALUE IN IT. The company name
// is read from Company Settings rather than typed in, so another company using
// Stede downloads the same workbook with their own name on it and nothing else
// changes. Colby's reasoning: "it may be used by more than d&r in the future."
export async function buildTemplateFile(descriptor, reference) {
  if (!descriptor.workbookSpec) {
    return {
      blob: new Blob([descriptor.templateCsv()], { type: 'text/csv;charset=utf-8;' }),
      name: descriptor.templateName,
    };
  }
  const { data } = await supabase
    .from('company_settings').select('company_name').limit(1).maybeSingle();
  const spec = descriptor.workbookSpec(reference || {});
  const bytes = await buildImportWorkbook({
    ...spec,
    label: descriptor.label,
    columns: descriptor.columns,
    // A blank name would read as an unfinished file rather than a generic one,
    // so it falls back to the product's own name.
    company: (data && data.company_name) || 'your company',
  });
  return {
    blob: new Blob([bytes], { type: WORKBOOK_MIME }),
    name: workbookFileName(descriptor.label),
  };
}

// ── The vendor list ──────────────────────────────────────────────────────────
//
// #194's importer, unchanged in behavior and rewritten only as a descriptor.
// It is the reference case: every field below already existed inside the
// component as a hard-coded assumption, and naming them is what made the second
// import type possible without a second screen.
const vendorsDescriptor = {
  key: 'vendors',
  label: 'Vendor list',
  what: 'Who D&R buys from — one row per company, with contact details and payment terms.',
  noun: { one: 'vendor', many: 'vendors' },
  columns: VENDOR_COLUMNS,
  templateName: 'Stede_Vendor_Import_Template.csv',
  templateCsv: () => templateCsv(VENDOR_COLUMNS),

  // M15. Nothing in permissions.js changes for any of this — the descriptor
  // names the predicate that already governs the surface it writes to.
  can: auth => canImportVendorCatalog(auth),

  findHeaderRow: vendorList.findHeaderRow,
  mapHeaders: vendorList.mapHeaders,

  async loadReference() {
    // Every column the import can touch, so the preview can diff against what
    // is actually stored rather than against what the list happens to render —
    // the page's own query does not select the address block.
    const [{ data: v, error: vErr }, { data: b }] = await Promise.all([
      supabase.from('inventory_vendors').select(`
        id, name, contact_name, email, phone,
        street_address, address_line2, city, state, postal_code,
        payment_terms, quickbooks_vendor_name,
        notes, is_internal, branch_id, is_active
      `),
      supabase.from('branches').select('id, name'),
    ]);
    if (vErr) throw vErr;
    return { existing: v || [], branches: b || [] };
  },

  // D6. Straight to the preview when there is nothing to ask about.
  //
  // Takes the SETTLED key set — what the file matched automatically plus
  // whatever the person mapped by hand — rather than the raw header info, so a
  // required column supplied through the mapping step counts. Reading the
  // automatic mapping alone would refuse a file the person had just fixed.
  mappingBlocker(found) {
    if (!found.has('name')) {
      return 'No column in that file holds the vendor name, so there is nothing to import against. Export the catalog to see the format.';
    }
    return null;
  },

  // Nothing in a vendor list is a governed vocabulary, so this import asks no
  // value questions and the step is skipped. Returning an empty list rather
  // than the component checking the type is the whole point of M22.
  collectQuestions: () => [],

  buildPlan({ rows, headerRowIndex, mapping, reference }) {
    const plan = vendorList.buildImportPlan({
      rows, headerRowIndex, mapping,
      existing: reference.existing, branches: reference.branches,
    });
    vendorList.annotateSimilarity(plan, reference.existing);
    return plan;
  },

  execute: (plan, ctx, onProgress) => vendorList.executeImportPlan(plan, onProgress),

  describeRow(entry) {
    if (entry.mode === 'error') return entry.errors.join(' ');
    if (entry.mode === 'create') return 'New vendor';
    if (entry.mode === 'update') {
      return `${entry.changes.length} of ${VENDOR_COLUMNS.length - 1} fields change · ${entry.changes.map(c => c.label).join(', ')}`;
    }
    return 'Nothing in the file differs from what Stede already has';
  },

  // Callout K. Says so and changes nothing, so a false positive costs a
  // sentence rather than data.
  banner(info) {
    if (!info?.looksQuickBooks) return null;
    return {
      title: 'This looks like a QuickBooks vendor export.',
      body: 'Names will match QuickBooks exactly, so linking suppliers on the Integrations page afterwards should be one press.',
    };
  },

  chooseHelp: [
    {
      title: 'Adding vendors Stede does not have yet?',
      body: 'Start from a blank template. It carries the columns and nothing else, so leave Stede ID empty and each row is added as a new vendor.',
      download: true,
    },
    {
      title: 'Changing vendors it already has?',
      body: 'Export the catalog instead — that file is the import format, it arrives with the Stede ID already filled in, and you can edit it and hand it straight back.',
    },
  ],

  footnote: 'Vendors already in Stede but missing from this file are left untouched — an import is not a sync.',
};

// ── A vendor's own catalog ───────────────────────────────────────────────────
//
// M2. What each supplier sells and what they charge. Most of these rows are for
// products D&R does not stock, and that is the point rather than a shortcoming
// — it is the hedge that means the next "one-time" order is already priced.
const vendorCatalogDescriptor = {
  key: 'vendorCatalog',
  label: 'Vendor catalog',
  what: 'What one vendor sells and what they charge — a supplier’s price list, whether or not we stock the products on it.',
  noun: { one: 'price', many: 'prices' },
  columns: VENDOR_CATALOG_COLUMNS,
  templateName: 'Stede_Vendor_Pricing_Import_Template.csv',
  templateCsv: () => templateCsv(VENDOR_CATALOG_COLUMNS),

  // Session 78u, gate W3. The purchasing palette is NOT the inventory one with
  // a different hue: white on Purchasing orange measures 2.17:1 and fails even
  // the large-text bar, so orange is a FILL WITH DARK TEXT here and never text
  // on white. Assuming the two domain colours behaved the same would have
  // shipped an unreadable heading row.
  workbookSpec(reference) {
    const items = (reference.items || []).filter(i => i.sku);
    return {
      palette: 'purchasing',
      listNoun: 'vendors, package types and item codes',
      tips: [
        "The price is for the WHOLE pack. A bundle of 5 sheets at $247.50 is 247.50 and 5 - not 49.50. Stede works out the price each, and will refuse a column that tries to set it.",
        "Every row needs the vendor's own part number. A blank one is refused rather than skipped, so you cannot believe four hundred rows loaded when some did not.",
        'Leave Item SKU blank for anything D&R does not stock. That is the point of this sheet - the price is there before somebody orders it a second time.',
      ],
      lists: [
        { header: 'Vendor name', for: 'vendor_id',
          values: (reference.vendors || [])
            .filter(v => v.is_active !== false).map(v => v.name) },
        { header: 'Package type', for: 'pack_unit',
          values: (reference.units || []).map(u => u.name) },
        { header: 'Item SKU', for: 'item_id', values: items.map(i => i.sku) },
        { header: 'What that item is', values: items.map(i => i.name) },
      ],
    };
  },

  // M15. Jennifer can load a vendor's entire catalog and still cannot decide
  // what D&R stocks — the governance boundary, drawn where it already was.
  can: auth => canEditVendorPrice(auth),

  findHeaderRow: vendorCatalog.findHeaderRow,
  mapHeaders: vendorCatalog.mapHeaders,

  async loadReference() {
    const [vendorsRes, itemsRes, viRes, unitsRes] = await Promise.all([
      supabase.from('inventory_vendors').select('id, name, is_active'),
      supabase.from('inventory_items').select('id, name, sku'),
      supabase.from('vendor_items').select(
        'id, vendor_id, item_id, vendor_item_number, vendor_price, pack_qty, pack_unit, off_catalog_description, is_preferred, price_locked, is_provisional'
      ),
      fetchUnits({ activeOnly: true, kind: 'package' }),
    ]);
    if (vendorsRes.error) throw vendorsRes.error;
    if (itemsRes.error) throw itemsRes.error;
    if (viRes.error) throw viRes.error;
    if (unitsRes.error) throw unitsRes.error;
    return {
      vendors: vendorsRes.data || [],
      items: itemsRes.data || [],
      vendorItems: viRes.data || [],
      units: unitsRes.rows || [],
    };
  },

  mappingBlocker(found) {
    const missing = vendorCatalog.REQUIRED_KEYS.filter(k => !found.has(k));
    if (!missing.length) return null;
    const names = missing.map(k => (VENDOR_CATALOG_COLUMNS.find(c => c.key === k) || {}).header || k);
    return `That file is missing ${names.join(' and ')}. A price list needs the vendor, their part number and the price before Stede can do anything with it. Download the blank template to see the format.`;
  },

  // #468 layers 1 and 2. The live list is read at import time, so unlike a
  // drop-down baked into a downloaded file it cannot go stale (M6), and an
  // unrecognized value becomes one question that fixes every row using it
  // rather than a rejection per row (M7).
  collectQuestions({ rows, headerRowIndex, mapping, reference }) {
    return vendorCatalog.collectUnknownUnits({
      rows, headerRowIndex, mapping, units: reference.units,
    });
  },

  // M8. A new PACKAGE TYPE may be added during an import; a new CATEGORY may
  // not. Units already have canAddUnit, shipped at 78n2 on Colby's own
  // requirement that Jennifer must not sit mid-order waiting for a unit, so
  // narrowing it inside the importer would create a second contradictory rule
  // for the same act. Categories are a decision about the shape of the catalog
  // rather than an unblock, and are added only in Company Settings — which is
  // why the item descriptor at 78t will answer this differently for the same
  // question shape.
  canCreateValue: () => true,

  questionOptions(question, reference) {
    return (reference.units || []).map(u => ({ value: u.name, label: u.name }));
  },

  buildPlan({ rows, headerRowIndex, mapping, reference, valueMap }) {
    return vendorCatalog.buildVendorItemsPlan({
      rows, headerRowIndex, mapping,
      vendors: reference.vendors,
      items: reference.items,
      vendorItems: reference.vendorItems,
      units: reference.units,
      unitMap: valueMap,
    });
  },

  execute(plan, { questions, valueMap }, onProgress) {
    return vendorCatalog.executeVendorItemsPlan(
      plan,
      { newUnits: vendorCatalog.unitsToCreate(questions, valueMap) },
      onProgress,
    );
  },

  describeRow(entry) {
    if (entry.mode === 'error') return entry.errors.join(' ');
    const where = entry.vendorName ? `${entry.vendorName} · ${entry.partNumber}` : entry.partNumber;
    if (entry.mode === 'create') {
      return entry.itemName
        ? `${where} — priced against ${entry.itemName}`
        : `${where} — this vendor sells it; we do not stock it`;
    }
    if (entry.mode === 'update') {
      const linking = entry.linksNewItem ? ' · now linked to ' + entry.itemName : '';
      return `${where} · ${entry.changes.map(c => c.label).join(', ')}${linking}`;
    }
    return `${where} — nothing in the file differs from what Stede already has`;
  },

  chooseHelp: [
    {
      title: 'Loading a vendor’s price list?',
      body: 'Start from the blank template. Every row needs the vendor’s name, their part number and the price — the Stede SKU is optional, and leaving it blank means they sell it and we do not stock it.',
      download: true,
    },
    {
      title: 'The price is the PACK price, not the price each.',
      body: 'If Grimco sells a bundle of 5 sheets for $247.50, that is 247.50 with 5 units per pack — not 49.50. Stede works out the cost per unit itself, and wanting a “price each” column is the signal that the other two are wrong.',
    },
  ],

  footnote: 'Products this vendor sells that are missing from the file are left untouched, and no other vendor is affected — an import never removes a vendor from an item.',
};

// ── The item catalog ─────────────────────────────────────────────────────────
//
// #422's item half, Session 78t. What D&R actually stocks — the rows the whole
// Inventory module is about.
//
// THIS DESCRIPTOR IS M22's TEST CASE and the claim above held: it is a new
// object in this file and MasterDataImportSheet.js was not edited. The three
// places it behaves differently from the vendor sheets are all expressed as
// data below, which is exactly what the split was drawn for.
const itemsDescriptor = {
  key: 'items',
  label: 'Item catalog',
  what: 'What D&R stocks — one row per item, with the category it files under and what one of it is.',
  noun: { one: 'item', many: 'items' },
  columns: ITEM_COLUMNS,
  templateName: 'Stede_Item_Import_Template.csv',
  templateCsv: () => templateCsv(ITEM_COLUMNS),

  // Session 78u, gate W1-W18. The workbook is generated from the SAME column
  // contract the CSV uses, so the two cannot describe different files.
  //
  // THE CATEGORY LIST IS DISAMBIGUATED (W8, and #483 is why): two subcategories
  // may share a name, so a bare "Cast" tells Stede nothing. `indexCategories`
  // is the same function buildItemsPlan resolves against, so every value this
  // drop-down offers is one the importer can resolve - by construction rather
  // than by care.
  workbookSpec(reference) {
    const cats = (reference.categories || []).filter(c => c.is_active !== false);
    const byId = new Map(cats.map(c => [String(c.id), c]));
    const choices = itemCatalog.indexCategories(cats).choices;
    return {
      palette: 'inventory',
      listNoun: 'categories, units and vendors',
      tips: [
        'Leave SKU blank and Stede writes the code for you. Only fill it in if you already have a part number you want kept.',
        'File items under the subcategory, not the group above it. Dibond, not Sheeted Materials.',
        `Do not rename the headings on the ${'Item catalog'} tab, and do not edit the Lists tab. Everything else is yours to move.`,
      ],
      lists: [
        { header: 'Category', for: 'category_id', values: choices.map(c => c.value) },
        // No `for`, so it feeds no drop-down. It is here because two categories
        // can be called the same thing and this column is where a typist
        // discovers the shape of the catalog.
        { header: 'Under', values: choices.map(c => {
          const cat = byId.get(String(c.id));
          const parent = cat && cat.parent_id ? byId.get(String(cat.parent_id)) : null;
          return parent ? parent.name : '';
        }) },
        { header: 'Stocking unit', for: 'uom', values: (reference.units || []).map(u => u.name) },
        { header: 'Vendors in Stede', for: 'vendor_id',
          values: itemCatalog.indexVendors(
            (reference.vendors || []).filter(v => v.is_active !== false)
          ).choices.map(v => v.value) },
      ],
    };
  },

  // M15. Deciding what D&R stocks is a governance act, so this is the narrower
  // of the two permissions: admin, manager and executive. Purchasing can load a
  // vendor's entire price list and still cannot add an item to the catalog.
  can: auth => canGovernCatalogItem(auth),

  findHeaderRow: itemCatalog.findHeaderRow,
  mapHeaders: itemCatalog.mapHeaders,

  async loadReference() {
    // `code` and `parent_id` on the categories are NOT optional here, and this
    // is the 78s.3 lesson written into a query: generateSkuForCategory() builds
    // the code from the parent's code and the child's, so a select that omits
    // them returns a category list that looks complete and generates nothing.
    // A migration adding a column changes no screen on its own.
    const [itemsRes, catsRes, unitsRes, vendorsRes, branchesRes] = await Promise.all([
      supabase.from('inventory_items')
        .select('id, name, sku, category_id, uom, description, vendor_id, is_active'),
      fetchCategories(),
      fetchUnits({ activeOnly: true, kind: 'measure' }),
      supabase.from('inventory_vendors').select('id, name, is_active'),
      supabase.from('branches').select('id, name'),
    ]);
    if (itemsRes.error) throw itemsRes.error;
    if (catsRes.error) throw catsRes.error;
    if (unitsRes.error) throw unitsRes.error;
    if (vendorsRes.error) throw vendorsRes.error;
    if (branchesRes.error) throw branchesRes.error;
    return {
      items: itemsRes.data || [],
      categories: catsRes.rows || catsRes.data || [],
      units: unitsRes.rows || [],
      vendors: vendorsRes.data || [],
      branches: branchesRes.data || [],
    };
  },

  mappingBlocker(found) {
    const missing = itemCatalog.REQUIRED_KEYS.filter(k => !found.has(k));
    if (!missing.length) return null;
    const names = missing.map(k => (ITEM_COLUMNS.find(c => c.key === k) || {}).header || k);
    return `That file is missing ${names.join(' and ')}. An item needs a name, a category and a stocking unit before Stede can add it. The SKU is optional — leave it blank and Stede writes the code itself. Download the blank template to see the format.`;
  },

  collectQuestions({ rows, headerRowIndex, mapping, reference }) {
    return itemCatalog.collectUnknownValues({
      rows, headerRowIndex, mapping,
      categories: reference.categories,
      units: reference.units,
      vendors: reference.vendors,
    });
  },

  // M8, and the difference importDescriptors.js predicted at 78s.1: a new
  // STOCKING UNIT may be added mid-import, a new CATEGORY may not, and neither
  // may a new VENDOR. A unit is an unblock — Colby's 78n2 rule that nobody
  // should sit waiting for one. A category is a decision about the shape of the
  // catalog, made in Company Settings by somebody looking at the whole list. A
  // vendor has an importer of its own two rows up this file.
  canCreateValue: q => q.field === 'uom',

  // Session 78u. These used to be built here as `{ value: c.name }`, which had
  // the same defect as the lookup it feeds: two categories called "Cast" gave
  // two options both reading "Cast" and both writing "Cast" as the answer, so
  // picking one resolved no better than typing it.
  //
  // The choices now come from the same index buildItemsPlan resolves against,
  // so EVERY offered value is a key that index holds - an option that cannot
  // resolve is now impossible by construction rather than by care. Where the
  // name is ambiguous the option reads "Cast (Cast Vinyl)"; where it is not, it
  // reads exactly as before.
  questionOptions(question, reference) {
    if (question.field === 'category_id') {
      return itemCatalog
        .indexCategories((reference.categories || []).filter(c => c.is_active !== false))
        .choices.map(({ value, label }) => ({ value, label }));
    }
    if (question.field === 'vendor_id') {
      return itemCatalog
        .indexVendors((reference.vendors || []).filter(v => v.is_active !== false))
        .choices.map(({ value, label }) => ({ value, label }));
    }
    return (reference.units || []).map(u => ({ value: u.name, label: u.name }));
  },

  // NOTE the argument list, which is the whole of what the screen supplies:
  // MasterDataImportSheet.js calls this with rows, headerRowIndex, mapping,
  // reference and valueMap — and NOT with the questions. buildItemsPlan
  // resolves answers from valueMap alone for that reason. Asking for
  // `questions` here silently yields undefined and every answer is ignored,
  // which is a defect the unit tests cannot see and the live test found.
  buildPlan({ rows, headerRowIndex, mapping, reference, valueMap }) {
    return itemCatalog.buildItemsPlan({
      rows, headerRowIndex, mapping,
      items: reference.items,
      categories: reference.categories,
      units: reference.units,
      vendors: reference.vendors,
      branches: reference.branches,
      valueMap,
    });
  },

  execute(plan, { reference, questions, valueMap }, onProgress) {
    return itemCatalog.executeItemsPlan(
      plan,
      {
        newUnits: itemCatalog.unitsToCreate(questions, valueMap),
        branches: reference.branches || [],
      },
      onProgress,
    );
  },

  describeRow(entry) {
    if (entry.mode === 'error') return entry.errors.join(' ');
    if (entry.mode === 'create') {
      const sku = entry.skuSource === 'generated'
        ? `Stede writes the SKU ${entry.values.sku}`
        : `SKU ${entry.values.sku}`;
      const stock = entry.branchCount
        ? `, and a stock row at each of the ${entry.branchCount} branches`
        : '';
      return `New item · ${sku}${stock}`;
    }
    if (entry.mode === 'update') {
      return `${entry.changes.length} of ${ITEM_COLUMNS.length - 1} fields change · ${entry.changes.map(c => c.label).join(', ')}`;
    }
    return 'Nothing in the file differs from what Stede already has';
  },

  chooseHelp: [
    {
      title: 'Adding items Stede does not stock yet?',
      body: 'Start from a blank template. Leave Stede ID empty and each row is added as a new item — and leave the SKU empty too, because Stede builds the code from the category and the name, exactly as the Add-item form does.',
      download: true,
    },
    {
      title: 'A category has to exist before the import runs.',
      body: 'An import never invents one, because a category decides the shape of the catalog and the SKU is built from its code. Add it in Company Settings first. A stocking unit is different — an unrecognised one can be added while the import is being set up.',
    },
  ],

  footnote: 'Items already in Stede but missing from this file are left untouched — an import is not a sync. Adding an item also gives it a stock row at every branch, starting at zero.',
};

export const IMPORT_TYPES = [itemsDescriptor, vendorsDescriptor, vendorCatalogDescriptor];

export function importTypesFor(auth) {
  return IMPORT_TYPES.filter(d => d.can(auth));
}

export function descriptorByKey(key) {
  return IMPORT_TYPES.find(d => d.key === key) || null;
}
